<template>
    <div>
        <RecordDialog v-if="showRecordDialog" v-bind="recordDialogParameters"
                      v-on:hidden="showRecordDialog = false"
                      v-on:ok="setData"/>

        <div class="card st-framework-card">
            <div class="card-header">
                <table style="border-collapse: collapse; width: 100%;">
                    <tbody>
                    <tr>
                        <td style="margin: 0; padding: 0 0 0 1rem; width: 50px;">
                            <font-awesome-icon :icon="icon" size="sm"/>
                        </td>
                        <td style="text-align: center;">
                            {{ $i18n.tc('translations.' + name, 2) }}
                        </td>
                        <td style="width: 50px;"/>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div class="card-body st-fadein-content" style="padding-top: 0;" v-if="pageLoaded">

                <!-- STICKY HEADER -->

                <div class="sticky-header">

                    <!-- HEADER BUTTONS -->

                    <table class="table table-borderless m-0 mb-3">
                        <tbody>
                        <tr>
                            <td class="p-0">
                                <template v-for="(action,actionIndex) in actions.table">
                                    <template v-if="action.placement === 'TABLEHEADERLEFT'">
                                        <DropDownAction :key="action.name+'-'+actionIndex" :action="action" :disabled="state.disabled"
                                                        v-on:add-order="addOrder"
                                                        v-on:add-product="addProduct"
                                                        v-on:clear-orders="setConfig({orders: null})"
                                                        v-on:clear-products="setConfig({products: null})"
                                                        v-on:clear-orders-and-products="setConfig({orders: null, products: null})"
                                                        v-on:get-orders-from-work-plan="getWorkPlanData({orders: true})"
                                                        v-on:get-products-for-orders="getProductsForOrders"
                                                        v-on:get-work-plan-data="getWorkPlanData({orders: true, products: true})"
                                                        v-if="action.type === 'DROPDOWN'"/>
                                    </template>
                                </template>
                            </td>
                            <td class="p-0">
                                <div class="float-right">
                                    <template v-for="(action,actionIndex) in actions.table">
                                        <template v-if="action.placement === 'TABLEHEADERRIGHT'">
                                            <ButtonAction :key="action.name+'-'+actionIndex" :action="action" :disabled="state.disabled"
                                                          v-on:refresh="fetchData"
                                                          v-if="action.type === 'BUTTON'"/>
                                        </template>
                                    </template>
                                </div>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <div :key="'header-'+reloadTable" class="row m-0 p-0" v-if="orders || products">

                        <!-- FIXED TABLE AREA -->

                        <div class="col fixed-table-area" style="margin: 16px 0 0 0;">
                            <table class="table inventory-forecast-table" style="height: 100%">
                                <thead>
                                <tr class="fixed-table-area-row">
                                    <th></th>
                                </tr>
                                </thead>
                            </table>
                        </div>

                        <!-- SCROLLABLE TABLE AREA -->

                        <div ref="scrollable-table-area" class="col scrollable-table-area">
                            <div ref="top-scrollbar-wrapper" class="top-scrollbar-wrapper"
                                 v-on:scroll="scrollTopScrollBar">
                                <div class="top-scrollbar" :style="top_scrollbar_style"/>
                            </div>
                            <div ref="scrollable-header-wrapper" style="overflow: hidden;">
                                <table ref="scrollarea" class="table table-borderless inventory-forecast-table">
                                    <thead>
                                    <tr>
                                        <template v-for="(product,productIndex) in products">
                                            <th :key="'th-'+productIndex" :title="product.label">
                                                <table class="table table-borderless m-0" style="table-layout: fixed; width: 100%; height: 100%;">
                                                    <tbody>
                                                    <tr style="background: none;">
                                                        <th class="p-0" style="text-align: center; vertical-align: top; width: 50px;">
                                                            <button class="btn btn-outline-dark btn-sm px-1 py-0"
                                                                    v-on:click="shiftProductLeft(product)">
                                                                <font-awesome-icon :icon="['fas', 'chevron-left']" size="sm"/>
                                                            </button>
                                                            <button class="btn btn-outline-danger btn-sm px-1 py-0 ml-1"
                                                                    v-on:click="removeProduct(product)">
                                                                <font-awesome-icon :icon="['fas', 'minus']" size="sm"/>
                                                            </button>
                                                        </th>
                                                        <th class="p-0" style="text-align: right; vertical-align: top;">
                                                            <button class="btn btn-outline-dark btn-sm px-1 py-0"
                                                                    v-on:click="shiftProductRight(product)">
                                                                <font-awesome-icon :icon="['fas', 'chevron-right']" size="sm"/>
                                                            </button>
                                                        </th>
                                                    </tr>
                                                    <tr style="background: none;">
                                                        <th colspan="2" class="p-0" style="vertical-align: middle;">
                                                            <Field :field="fields.linkProduct" :data="product"/>
                                                        </th>
                                                    </tr>
                                                    <tr style="background: none;">
                                                        <th colspan="2" class="p-0" :style="product.inventory_style" style="vertical-align: bottom;">
                                                            {{ product.inventory }} {{ product.unit_label }}
                                                        </th>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </th>
                                        </template>
                                    </tr>
                                    </thead>
                                </table>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- TABLE -->

                <div :key="'table-'+reloadTable" class="row m-0 p-0" v-if="orders || products">

                    <!-- FIXED TABLE AREA -->

                    <div class="col fixed-table-area">
                        <table class="table table-striped inventory-forecast-table">
                            <tbody>
                            <template v-for="(order,orderIndex) in orders">
                                <tr :key="'tr-'+order.id+'-'+orderIndex" :class="{'selected-row': (order.id === selectedRow)}" class="fixed-table-area-row"
                                    v-on:click="selectTableRow(order)">
                                    <td :title="order.label">
                                        <table class="table table-borderless m-0" style="table-layout: fixed; width: 100%; height: 100%;">
                                            <tbody>
                                            <tr style="background: none;">
                                                <td class="p-0" style="text-align: center; vertical-align: middle; width: 50px;">
                                                    <button class="btn btn-outline-dark btn-sm px-1 py-0"
                                                            v-on:click="shiftOrderUp(order)">
                                                        <font-awesome-icon :icon="['fas', 'chevron-up']" size="sm"/>
                                                    </button>
                                                    <button class="btn btn-outline-danger btn-sm px-1 py-0 ml-1"
                                                            v-on:click="removeOrder(order)">
                                                        <font-awesome-icon :icon="['fas', 'minus']" size="sm"/>
                                                    </button>
                                                </td>
                                                <td class="p-0" style="text-align: center; vertical-align: middle;">
                                                    {{ order.delivery_date }}
                                                </td>
                                                <td class="p-0" style="text-align: right; vertical-align: middle; white-space: nowrap;">
                                                    <ToggleAction :key="actions.toggleManagedOrder.name+'-'+orderIndex"
                                                                  :action="actions.toggleManagedOrder" :disabled="state.disabled"
                                                                  :parameter-data="order"
                                                                  v-on:toggle-managed-order="toggleManagedOrder(order)"/>
                                                    <button class="btn btn-outline-dark btn-sm px-1 py-0 ml-1"
                                                            v-on:click="shiftOrderDown(order)">
                                                        <font-awesome-icon :icon="['fas', 'chevron-down']" size="sm"/>
                                                    </button>
                                                </td>
                                            </tr>
                                            <tr style="background: none;">
                                                <td colspan="3" class="p-0" style="text-align: center; vertical-align: middle; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">
                                                    <Field :field="fields.linkOrder" :data="order"/>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </template>
                            </tbody>
                        </table>
                    </div>

                    <!-- SCROLLABLE TABLE AREA -->

                    <div ref="scrollable-table-area" class="col scrollable-table-area">
                        <div ref="scrollable-area-wrapper" class="scrollable-area-wrapper"
                             v-on:scroll="scrollBottomScrollBar">
                            <div ref="scrollable-area">
                                <table class="table table-striped inventory-forecast-table">
                                    <tbody>
                                    <template v-for="(order,orderIndex) in orders">
                                        <tr :key="'tr-'+order.id+'-'+orderIndex" :class="{'selected-row': (order.id === selectedRow)}"
                                            v-on:click="selectTableRow(order)">
                                            <template v-for="(product,productIndex) in products">
                                                <td :key="'td-'+productIndex+'-'+orderIndex">
                                                    <table class="table table-borderless m-0" style="table-layout: fixed; width: 100%; height: 100%;">
                                                        <tbody>
                                                        <tr style="background: none;">
                                                            <td class="p-0" style="text-align: center; vertical-align: middle;">
                                                                &nbsp;
                                                                <template v-if="hasInventoryData(product.id, order.id)">
                                                                    {{ inventoryData(product.id, order.id, 'needed') }} ({{ inventoryData(product.id, order.id, 'used') }})
                                                                </template>
                                                                <template v-else>
                                                                    &nbsp;
                                                                </template>
                                                            </td>
                                                        </tr>
                                                        <tr style="background: none;">
                                                            <td class="p-0" :style="inventoryData(product.id, order.id, 'style')" style="text-align: center; vertical-align: middle;">
                                                                <b>{{ inventoryData(product.id, order.id, 'inventory') }}</b>
                                                            </td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </template>
                                        </tr>
                                    </template>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import ButtonAction from "@/components/actions/ButtonAction.vue";
import DropDownAction from "@/components/actions/DropDownAction";
import Field from "@/components/fields/Field.vue";
import FileDownload from "@/mixins/FileDownload";
import RecordDialog from "@/components/dialogs/RecordDialog.vue";
import ToggleAction from "@/components/actions/ToggleAction.vue";

export default {
    name: "InventoryForecast",
    components: {
        ButtonAction,
        DropDownAction,
        Field,
        RecordDialog,
        ToggleAction,
    },
    props: ['name', 'icon', 'api', 'state', 'userLanguage', 'languages', 'permissions'],
    mixins: [FileDownload],
    data() {
        return {
            pageLoaded: false,
            actions: null,
            fields: null,
            products: null,
            orders: null,
            data: null,
            filter: '',
            selectedRow: null,
            top_scrollbar_style: '',
            reloadTable: 0,
            showRecordDialog: false,
            recordDialogParameters: {
                type: null,
                title: null,
                api: null,
                fields: null,
                languages: this.languages,
                userLanguage: this.userLanguage,
            },
        }
    },
    created() {
        this.fetchData();
    },
    methods: {
        addOrder() {
            this.recordDialogParameters.type = "create";
            this.recordDialogParameters.title = this.$i18n.tc("translations.Order", 1);
            this.recordDialogParameters.api = this.api + '/add_order';
            this.recordDialogParameters.fields = this.fields.addOrder;
            this.showRecordDialog = true;
        },
        addProduct() {
            this.recordDialogParameters.type = "create";
            this.recordDialogParameters.title = this.$i18n.tc("translations.Product", 1);
            this.recordDialogParameters.api = this.api + '/add_product';
            this.recordDialogParameters.fields = this.fields.addProduct;
            this.showRecordDialog = true;
        },
        fetchData() {
            this.state.loading = true;
            this.$http.get(this.api + '/manage', {}).then((res) => {
                this.setData(res.data);
            }).catch((error) => {
                console.log("InventoryForecast:fetchData():error:", error);
            });
        },
        getProductsForOrders() {
            this.state.loading = true;
            this.$http.get(this.api + '/get_products_for_orders', {}).then((res) => {
                this.setData(res.data);
            }).catch((error) => {
                console.log("InventoryForecast:getProductsForOrders():error:", error);
            });
        },
        getWorkPlanData(data) {
            this.state.loading = true;
            this.$http.post(this.api + '/get_work_plan_data', data).then((res) => {
                this.setData(res.data);
            }).catch((error) => {
                console.log("InventoryForecast:getWorkPlanData():error:", error);
            });
        },
        hasInventoryData(productId, orderId) {
            if (this.data && (productId in this.data) && (orderId in this.data[productId])) {
                return (this.data[productId][orderId]['needed'] !== 0 || this.data[productId][orderId]['used'] !== 0);
            }
            return false;
        },
        inventoryData(productId, orderId, key) {
            if (this.data && (productId in this.data) && (orderId in this.data[productId])) {
                return this.data[productId][orderId][key];
            }
            return "?";
        },
        removeOrder(order) {
            let orderIds = this.orders ? this.orders.map(o => o.id) : [];
            let index = orderIds.indexOf(order.id);
            orderIds.splice(index, 1);
            this.setConfig({orders: orderIds});
        },
        removeProduct(product) {
            let productIds = this.products ? this.products.map(p => p.id) : [];
            let index = productIds.indexOf(product.id);
            productIds.splice(index, 1);
            this.setConfig({products: productIds});
        },
        scrollBottomScrollBar() {
            let topScrollBar = this.$refs['top-scrollbar-wrapper'];
            let bottomScrollBar = this.$refs['scrollable-area-wrapper'];
            topScrollBar.scrollLeft = bottomScrollBar.scrollLeft;
        },
        scrollTopScrollBar() {
            let topScrollBar = this.$refs['top-scrollbar-wrapper'];
            let bottomScrollBar = this.$refs['scrollable-area-wrapper'];
            let headerScrollBar = this.$refs['scrollable-header-wrapper'];
            bottomScrollBar.scrollLeft = topScrollBar.scrollLeft;
            headerScrollBar.scrollLeft = topScrollBar.scrollLeft;
        },
        selectTableRow(order) {
            this.selectedRow = order.id;
        },
        setConfig(config) {
            this.state.loading = true;
            this.$http.post(this.api + '/set_config', config).then((res) => {
                this.setData(res.data);
            }).catch((error) => {
                console.log("InventoryForecast:setConfig():error:", error);
            });
        },
        setData(data) {
            if (Object.hasOwn(data, 'actions')) {
                this.actions = data.actions;
            }
            if (Object.hasOwn(data, 'fields')) {
                this.fields = data.fields;
            }
            if (Object.hasOwn(data, 'orders')) {
                this.orders = data.orders;
                this.data = null;
            }
            if (Object.hasOwn(data, 'products')) {
                this.products = data.products;
            }
            if (Object.hasOwn(data, 'data')) {
                this.data = data.data;
            }
            this.reloadTable++;
            this.pageLoaded = true;
            this.state.loading = false;

            this.$nextTick(function () {
                let scrollArea = this.$refs['scrollarea'];
                if (scrollArea) {
                    this.top_scrollbar_style = 'width: ' + scrollArea.clientWidth + 'px;';
                }
            });
        },
        shiftOrderDown(order) {
            let orderIds = this.orders ? this.orders.map(o => o.id) : [];
            let index = orderIds.indexOf(order.id);
            orderIds.splice(index, 1);
            if (index >= orderIds.length) {
                orderIds.splice(0, 0, order.id);
            } else {
                orderIds.splice(index + 1, 0, order.id);
            }
            this.setConfig({orders: orderIds});
        },
        shiftOrderUp(order) {
            let orderIds = this.orders ? this.orders.map(o => o.id) : [];
            let index = orderIds.indexOf(order.id);
            orderIds.splice(index, 1);
            if (index === 0) {
                orderIds.splice(orderIds.length, 0, order.id);
            } else {
                orderIds.splice(index - 1, 0, order.id);
            }
            this.setConfig({orders: orderIds});
        },
        shiftProductLeft(product) {
            let productIds = this.products ? this.products.map(p => p.id) : [];
            let index = productIds.indexOf(product.id);
            productIds.splice(index, 1);
            if (index === 0) {
                productIds.splice(productIds.length, 0, product.id);
            } else {
                productIds.splice(index - 1, 0, product.id);
            }
            this.setConfig({products: productIds});
        },
        shiftProductRight(product) {
            let productIds = this.products ? this.products.map(p => p.id) : [];
            let index = productIds.indexOf(product.id);
            productIds.splice(index, 1);
            if (index >= productIds.length) {
                productIds.splice(0, 0, product.id);
            } else {
                productIds.splice(index + 1, 0, product.id);
            }
            this.setConfig({products: productIds});
        },
        toggleManagedOrder(order) {
            order.managed_order = !order.managed_order;
            let managedOrdersIds = [];
            this.orders.forEach(order => {
                if (order.managed_order) {
                    managedOrdersIds.push(order.id);
                }
            });
            this.setConfig({managed_orders: managedOrdersIds});
        }
    }
}
</script>

<style scoped>
.fixed-table-area {
    width: 400px;
    min-width: 400px;
    max-width: 400px;
    padding: 0;
    z-index: 400;
}

.fixed-table-area-row > th:first-child,
.fixed-table-area-row > td:first-child {
    width: 400px;
}

.scrollable-area-wrapper {
    overflow-y: visible;
    overflow-x: scroll;
}

.top-scrollbar-wrapper {
    overflow-y: hidden;
    overflow-x: scroll;
    height: 16px;
}

.scrollable-table-area {
    width: calc(100% - 400px);
    margin: 0;
    padding: 0;
}

.top-scrollbar {
    height: 16px;
}

.sticky-header {
    position: sticky;
    top: 38px;
    background: white;
    padding-top: 1rem;
    z-index: 500;
}

.inventory-forecast-table {
    table-layout: fixed;
    border-collapse: collapse;
    margin: 0;
    border: 1px solid #dee2e6;
}

.inventory-forecast-table > thead > tr > th {
    width: 150px;
    height: 160px;
    text-align: center;
    margin: 0;
    border: 1px solid #dee2e6;
}

.inventory-forecast-table > tbody > tr > td {
    width: 150px;
    height: 60px;
    border: 1px solid #dee2e6;
}

.inventory-forecast-table > tbody > tr.selected-row {
    outline: 1px solid darkslategray;
    background: lightgreen;
}

a:link,
a:visited {
    color: black;
    text-decoration: none;
}

a:hover,
a:active {
    color: black;
    text-decoration: underline;
}
</style>